import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import './JobDetails.css';
import { useAuth } from '../contexts/AuthContext';

const JobDetails = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    experience: '',
    location: '',
    type: 'Full Time',
    shortDescription: '',
    description: '',
    workLocation: '',
    experienceLevel: ''
  });

  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await axios.get(`http://localhost:5001/api/jobs/${id}`);
        setJob(response.data);
        setFormData({
          title: response.data.title,
          experience: response.data.experience,
          location: response.data.location,
          type: response.data.type,
          shortDescription: response.data.shortDescription,
          description: response.data.description,
          workLocation: response.data.workLocation,
          experienceLevel: response.data.experienceLevel,
        });
      } catch (error) {
        setError('Error fetching job details');
      }
    };

    fetchJob();
  }, [id]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    try {
      await axios.put(`http://localhost:5001/api/jobs/${id}`, formData);
      setIsEditing(false);
      setJob({ ...job, ...formData });
    } catch (error) {
      setError('Error updating job');
    }
  };

  if (error) {
    return <p>{error}</p>;
  }

  if (!job) {
    return <p>Loading...</p>;
  }

  return (
    <Container className="my-5 job-details-container">
      <Card className="job-details-card">
        <Card.Body>
          {isEditing ? (
            <>
              <Form>
                <Form.Group controlId="formTitle" className="form-row">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group controlId="formExperience" className="form-row">
                  <Form.Label>Experience</Form.Label>
                  <Form.Control
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group controlId="formLocation" className="form-row">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group controlId="formType" className="form-row">
                  <Form.Label>Job Type</Form.Label>
                  <Form.Control
                    as="select"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                  >
                    <option value="Full Time">Full Time</option>
                    <option value="Part Time">Part Time</option>
                    <option value="Contract">Contract</option>
                    <option value="Freelance">Freelance</option>
                  </Form.Control>
                </Form.Group>

                <Form.Group controlId="formShortDescription" className="form-row">
                  <Form.Label>Short Description</Form.Label>
                  <Form.Control
                    type="text"
                    name="shortDescription"
                    value={formData.shortDescription}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group controlId="formDescription" className="form-row">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    required
                  />
                </Form.Group>

                <Form.Group controlId="formWorkLocation" className="form-row">
                  <Form.Label>Work Location</Form.Label>
                  <Form.Control
                    as="select"
                    name="workLocation"
                    value={formData.workLocation}
                    onChange={handleChange}
                    required
                  >
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="On-site">On-site</option>
                  </Form.Control>
                </Form.Group>

                <Form.Group controlId="formExperienceLevel" className="form-row">
                  <Form.Label>Experience Level</Form.Label>
                  <Form.Control
                    as="select"
                    name="experienceLevel"
                    value={formData.experienceLevel}
                    onChange={handleChange}
                    required
                  >
                    <option value="Internship">Internship</option>
                    <option value="Entry level">Entry level</option>
                    <option value="Associate">Associate</option>
                    <option value="Mid-Senior level">Mid-Senior level</option>
                    <option value="Director">Director</option>
                    <option value="Executive">Executive</option>
                  </Form.Control>
                </Form.Group>
              </Form>
              <Button variant="primary" onClick={handleSave}>
                Save
              </Button>
              <Button variant="secondary" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <header className="job-header">
                <Row>
                  <Col xs={12} md={8}>
                    <h2>{job.title}</h2>
                    <p className="company-name">ISIR.AI</p>
                    <p className="job-location">{job.location}</p>
                  </Col>
                  <Col xs={12} md={4} className="text-md-end">
                    <Button
                      variant="primary"
                      href={`mailto:career@isir.ai?subject=Application for ${job.title}`}
                    >
                      Apply
                    </Button>
                  </Col>
                </Row>
              </header>
              <section className="job-meta">
                <p><strong>Experience:</strong> {job.experience}</p>
                <p><strong>Type:</strong> {job.type}</p>
                <p><strong>Work Location:</strong> {job.workLocation}</p>
                <p><strong>Job Level:</strong> {job.experienceLevel}</p>
                <p><strong>Published Date:</strong> {new Date(job.publishDate).toLocaleDateString()}</p>
                <p><strong>Status:</strong> {job.status}</p>
              </section>
              <section className="job-description">
                <h4>Job Description</h4>
                <div dangerouslySetInnerHTML={{ __html: job.description }} />
              </section>
              <section className="company-info">
                <h4>About the company</h4>
                <p>
                  ISIR.AI is a leading provider of AI-based solutions, dedicated to transforming businesses through innovative technology and data-driven insights. Join us in our mission to drive digital transformation and create a smarter future.
                </p>
              </section>
              {currentUser && (
                <Button variant="primary" onClick={() => setIsEditing(true)}>
                  Modify
                </Button>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default JobDetails;
